import React, { useState, useCallback, useMemo } from 'react';
import { Loading } from '@components/atoms/Loading';
import { Button } from '@components/atoms/Button';
import { MaterialCatalogTable } from '@components/organisms/MaterialCatalogTable';
import { MaterialCatalogFormModal } from '@components/organisms/MaterialCatalogFormModal';
import { ReceiveStockModal } from '@components/organisms/ReceiveStockModal';
import { AdjustStockModal } from '@components/organisms/AdjustStockModal';
import { ConfirmationModal } from '@components/molecules/ConfirmationModal';
import { ExportPdfConfigModal } from '@components/organisms/ExportPdfConfigModal';
import { WarehouseLayout } from '@components/layout/WarehouseLayout';
import { useWarehouseContext } from '@components/layout/WarehouseContext';
import {
  useMaterialCatalog,
  useStockLevels,
  useMaterialCategories,
  useWarehouses,
  useCreateMaterialCatalogItem,
  useUpdateMaterialCatalogItem,
  useDeleteMaterialCatalogItem,
  useReceiveStock,
} from '@hooks/api/warehouse';
import {
  MaterialCatalog,
  MaterialCatalogCreateInput,
  MaterialCatalogUpdateInput,
} from '@models/warehouse.model';
import { MaterialWithStock } from '@components/organisms/MaterialCatalogTable';
import { InitialStockData } from '@components/organisms/MaterialCatalogFormModal';
import { generateMaterialCatalogPdf } from '@utils/materialCatalogPdf';
import { StockStatus } from '@components/atoms/StockBadge';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import RefreshIcon from '@mui/icons-material/Refresh';
import { toast } from 'react-toastify';
import { useTranslation } from '@hooks/useTranslation';

const MaterialCatalogContent: React.FC = () => {
  const tActionMessages = useTranslation('actionMessages');
  const { selectedWarehouse } = useWarehouseContext();

  // Modal states
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isReceiveModalOpen, setIsReceiveModalOpen] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState<MaterialCatalog | null>(null);
  const [materialToReceive, setMaterialToReceive] = useState<MaterialCatalog | null>(null);
  const [materialToDelete, setMaterialToDelete] = useState<MaterialCatalog | null>(null);
  const [isAdjustModalOpen, setIsAdjustModalOpen] = useState(false);
  const [materialToAdjust, setMaterialToAdjust] = useState<MaterialCatalog | null>(null);
  const [isExportConfigOpen, setIsExportConfigOpen] = useState(false);

  // API Hooks
  const {
    data: materials = [],
    isLoading: materialsLoading,
    isError: materialsError,
    refetch: refetchMaterials,
  } = useMaterialCatalog();

  const {
    data: stockLevels = [],
    isLoading: stockLevelsLoading,
    isError: stockLevelsError,
    refetch: refetchStockLevels,
  } = useStockLevels(selectedWarehouse?.warehouseId);

  const {
    data: categories = [],
    isLoading: categoriesLoading,
    isError: categoriesError,
    refetch: refetchCategories,
  } = useMaterialCategories();

  const {
    data: warehouses = [],
  } = useWarehouses();

  // Mutations
  const createMutation = useCreateMaterialCatalogItem();
  const updateMutation = useUpdateMaterialCatalogItem();
  const deleteMutation = useDeleteMaterialCatalogItem();
  const receiveStockMutation = useReceiveStock();

  // Consider errors as "done loading" to not block the UI
  const isLoading =
    (materialsLoading && !materialsError) ||
    (stockLevelsLoading && !stockLevelsError) ||
    (categoriesLoading && !categoriesError);

  // Compute materials with stock for PDF export
  const materialsWithStock = useMemo<MaterialWithStock[]>(() => {
    return materials.map((material) => {
      const materialStockLevels = stockLevels.filter(
        (sl) => sl.catalogId === material.catalogId
      );
      const totalOnHand = materialStockLevels.reduce(
        (sum, sl) => sum + (sl.quantityOnHand || 0), 0
      );
      const totalReserved = materialStockLevels.reduce(
        (sum, sl) => sum + (sl.quantityReserved || 0), 0
      );
      const totalAvailable = totalOnHand - totalReserved;
      const inventoryValue = totalOnHand * (material.averageCost || 0);

      let stockStatus: StockStatus = 'in_stock';
      if (totalAvailable <= 0) {
        stockStatus = 'out_of_stock';
      } else if (material.reorderPoint && totalAvailable <= material.reorderPoint) {
        stockStatus = 'low_stock';
      }

      return {
        ...material,
        totalOnHand,
        totalReserved,
        totalAvailable,
        inventoryValue,
        stockStatus,
      };
    });
  }, [materials, stockLevels]);

  // Handlers
  const handleRefresh = useCallback(() => {
    refetchMaterials();
    refetchStockLevels();
    refetchCategories();
  }, [refetchMaterials, refetchStockLevels, refetchCategories]);

  const handleAddMaterial = useCallback(() => {
    setSelectedMaterial(null);
    setIsFormModalOpen(true);
  }, []);

  const handleEditMaterial = useCallback((material: MaterialCatalog) => {
    setSelectedMaterial(material);
    setIsFormModalOpen(true);
  }, []);

  const handleReceiveStock = useCallback((material: MaterialCatalog) => {
    setMaterialToReceive(material);
    setIsReceiveModalOpen(true);
  }, []);

  const handleCloseReceiveModal = useCallback(() => {
    setIsReceiveModalOpen(false);
    setMaterialToReceive(null);
  }, []);

  const handleReceiveSuccess = useCallback(() => {
    refetchMaterials();
    refetchStockLevels();
  }, [refetchMaterials, refetchStockLevels]);

  const handleAdjustStock = useCallback((material: MaterialCatalog) => {
    setMaterialToAdjust(material);
    setIsAdjustModalOpen(true);
  }, []);

  const handleCloseAdjustModal = useCallback(() => {
    setIsAdjustModalOpen(false);
    setMaterialToAdjust(null);
  }, []);

  const handleAdjustSuccess = useCallback(() => {
    refetchMaterials();
    refetchStockLevels();
  }, [refetchMaterials, refetchStockLevels]);

  const handleCloseFormModal = useCallback(() => {
    setIsFormModalOpen(false);
    setSelectedMaterial(null);
  }, []);

  const handleSubmitForm = useCallback(
    async (
      data: MaterialCatalogCreateInput | MaterialCatalogUpdateInput,
      initialStock?: InitialStockData,
    ) => {
      try {
        if ('catalogId' in data && data.catalogId) {
          // Update
          await updateMutation.mutateAsync(data as MaterialCatalogUpdateInput);
          toast.success(tActionMessages.materialUpdatedSuccessfully);
        } else {
          // Create
          const created = await createMutation.mutateAsync(data as MaterialCatalogCreateInput);

          // If initial stock data provided, create a receive transaction
          if (initialStock && created?.catalogId) {
            try {
              await receiveStockMutation.mutateAsync({
                catalogId: created.catalogId,
                warehouseId: initialStock.warehouseId,
                quantity: initialStock.quantity,
                unitCost: initialStock.unitCost,
                notes: 'Initial stock on material creation',
              });
              toast.success(tActionMessages.materialCreatedWithInitialStock);
            } catch (stockError) {
              console.error('Error adding initial stock:', stockError);
              toast.success(tActionMessages.materialCreatedInitialStockFailed);
            }
          } else {
            toast.success(tActionMessages.materialCreatedSuccessfully);
          }
        }
        handleCloseFormModal();
      } catch (error) {
        console.error('Error saving material:', error);
        toast.error(tActionMessages.failedToSaveMaterial);
      }
    },
    [
      createMutation,
      updateMutation,
      receiveStockMutation,
      handleCloseFormModal,
      tActionMessages.materialUpdatedSuccessfully,
      tActionMessages.materialCreatedWithInitialStock,
      tActionMessages.materialCreatedInitialStockFailed,
      tActionMessages.materialCreatedSuccessfully,
      tActionMessages.failedToSaveMaterial,
    ]
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDeleteMaterial = useCallback((material: MaterialCatalog) => {
    setMaterialToDelete(material);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!materialToDelete) return;

    try {
      await deleteMutation.mutateAsync(materialToDelete.catalogId);
      toast.success(tActionMessages.materialDeletedSuccessfully);
      setMaterialToDelete(null);
    } catch (error) {
      console.error('Error deleting material:', error);
      toast.error(tActionMessages.failedToDeleteMaterial);
    }
  }, [
    materialToDelete,
    deleteMutation,
    tActionMessages.materialDeletedSuccessfully,
    tActionMessages.failedToDeleteMaterial,
  ]);

  const handleCancelDelete = useCallback(() => {
    setMaterialToDelete(null);
  }, []);

  const handleOpenExportConfig = useCallback(() => {
    setIsExportConfigOpen(true);
  }, []);

  const handleExportReport = useCallback(
    (options: { includeCostAndValue: boolean }) => {
      try {
        generateMaterialCatalogPdf({
          materials: materialsWithStock,
          categories,
          includeCostAndValue: options.includeCostAndValue,
        });
        toast.success(tActionMessages.materialCatalogExportedSuccessfully);
      } catch (error) {
        console.error('Error exporting material catalog:', error);
        toast.error(tActionMessages.failedToExportMaterialCatalog);
      }
    },
    [
      materialsWithStock,
      categories,
      tActionMessages.materialCatalogExportedSuccessfully,
      tActionMessages.failedToExportMaterialCatalog,
    ],
  );

  if (materialsLoading && !materialsError) {
    return (
      <div className="flex justify-center items-center h-full">
        <Loading size="xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Material Catalog
            </h1>
            <p className="text-sm text-gray-500">
              Manage your organization's material inventory
            </p>
          </div>

          <div className="flex gap-2">
            <Button
              variant="secondary"
              leftIcon={<FileDownloadIcon sx={{ fontSize: 16 }} />}
              onClick={handleOpenExportConfig}
            >
              Export PDF
            </Button>
            <Button
              variant="primary"
              leftIcon={<AddIcon sx={{ fontSize: 16 }} />}
              onClick={handleAddMaterial}
            >
              Add Material
            </Button>
            <Button variant="tertiary" onClick={handleRefresh}>
              <RefreshIcon sx={{ fontSize: 16 }} />
            </Button>
          </div>
        </div>

        {/* Content */}
        <MaterialCatalogTable
          materials={materials}
          stockLevels={stockLevels}
          categories={categories}
          isLoading={isLoading}
          onEdit={handleEditMaterial}
          onReceiveStock={handleReceiveStock}
          onAdjustStock={handleAdjustStock}
          onAddMaterial={handleAddMaterial}
        />

        {/* Form Modal */}
        <MaterialCatalogFormModal
          isOpen={isFormModalOpen}
          onClose={handleCloseFormModal}
          onSubmit={handleSubmitForm}
          material={selectedMaterial}
          categories={categories}
          warehouses={warehouses}
          existingMaterials={materials}
          isLoading={createMutation.isPending || updateMutation.isPending}
        />

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          isOpen={!!materialToDelete}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          title="Delete Material"
          description={`Are you sure you want to delete "${materialToDelete?.name}"? This action cannot be undone.`}
          confirmText="Delete"
          variant="danger"
        />

        {/* Receive Stock Modal */}
        <ReceiveStockModal
          isOpen={isReceiveModalOpen}
          onClose={handleCloseReceiveModal}
          initialCatalogId={materialToReceive?.catalogId}
          onSuccess={handleReceiveSuccess}
        />

        {/* Adjust Stock Modal */}
        <AdjustStockModal
          isOpen={isAdjustModalOpen}
          onClose={handleCloseAdjustModal}
          initialCatalogId={materialToAdjust?.catalogId}
          onSuccess={handleAdjustSuccess}
        />

        {/* Export PDF Config Modal */}
        <ExportPdfConfigModal
          isOpen={isExportConfigOpen}
          onClose={() => setIsExportConfigOpen(false)}
          onExport={handleExportReport}
        />
      </div>
  );
};

const MaterialCatalogPage: React.FC = () => {
  return (
    <WarehouseLayout>
      <MaterialCatalogContent />
    </WarehouseLayout>
  );
};

export default MaterialCatalogPage;
